import { defineStore } from 'pinia'
import { ref } from 'vue'
import { supabase } from '../lib/supabase'
import type { Tables } from '../../../../types'

type Opdrachtgever = Partial<Tables<'opdrachtgevers'>>

/** Supabase returns PostgrestError as a plain object, so instanceof Error misses its message. */
function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'object' && err !== null && typeof (err as { message?: unknown }).message === 'string') {
    return (err as { message: string }).message
  }
  return 'Unknown error'
}

export const useOpdrachtgeverStore = defineStore('opdrachtgever', () => {
  const opdrachtgever = ref<Opdrachtgever | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Load the opdrachtgever row of a portal user. Returns null when there is
   * none: the portal also serves installateurs (see useUserTypeCheck), who have
   * no row in opdrachtgevers, so "no row" is a normal state and not an error.
   */
  const getOpdrachtgeverById = async (id: string): Promise<Opdrachtgever | null> => {
    try {
      loading.value = true
      error.value = null

      const { data, error: queryError } = await supabase
        .from('opdrachtgevers')
        .select(`
          id,
          naam,
          contactnaam,
          straat,
          huisnr,
          postcode,
          plaats,
          telnr
        `)
        .eq('id', id)
        .maybeSingle()

      if (queryError) throw queryError

      opdrachtgever.value = data ?? null
      return data ?? null
    } catch (err) {
      error.value = errorMessage(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateOpdrachtgever = async (id: string, updates: Partial<Opdrachtgever>): Promise<void> => {
    try {
      loading.value = true
      error.value = null

      const { error: updateError } = await supabase
        .from('opdrachtgevers')
        .update({
          naam: updates.naam,
          contactnaam: updates.contactnaam,
          straat: updates.straat,
          huisnr: updates.huisnr,
          postcode: updates.postcode,
          plaats: updates.plaats,
          telnr: updates.telnr
        })
        .eq('id', id)

      if (updateError) throw updateError

      // Update local state
      if (opdrachtgever.value) {
        opdrachtgever.value = { ...opdrachtgever.value, ...updates }
      }
    } catch (err) {
      error.value = errorMessage(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    opdrachtgever,
    loading,
    error,
    getOpdrachtgeverById,
    updateOpdrachtgever
  }
})
